import React, { useCallback, useMemo, FC } from 'react'
import { useTheme, getContrastRatio, rgbToHex } from '@mui/material/styles';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import * as colors from '@mui/material/colors'
import Grid from '@mui/material/Grid'
import Input from '@mui/material/Input'
import Radio from '@mui/material/Radio'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import CheckIcon from '@mui/icons-material/Check'
import Slider from '@mui/material/Slider'
import { isRgb } from '../../utils/color'

import {
  IForm_Color,
} from '@kaidam/fc-shared/src/types/schema/form'

const colorMap = colors as {
  [name: string]: Record<string, string>,
}

const hues = Object.keys(colorMap).slice(1, 20)

const shades = ['900', '800', '700', '600', '500', '400', '300', '200', '100', '50', 'A700', 'A400', 'A200', 'A100']

const useStyles = makeStyles(theme => createStyles({
  radio: {
    padding: 0,
  },
  radioIcon: {
    width: 48,
    height: 48,
    border: '1px solid black',
    margin: '1px',
  },
  radioIconSelected: {
    width: 48,
    height: 48,
    border: '1px solid black',
    margin: '1px',
    color: theme.palette.common.white,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  swatch: {
    width: '100%',
  },
  sliderContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  slider: {
    width: 'calc(100% - 80px)',
    marginLeft: theme.spacing(3),
    marginRight: theme.spacing(3),
  },
  colorBar: {
    marginTop: theme.spacing(2),
  },
  colorSquare: {
    width: 64,
    height: 64,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: '1px solid black',
    margin: '1px',
    cursor: 'pointer',
  },
  button: {
    marginLeft: theme.spacing(1),
  },
}))

/*

  value:
    color
    hue
    shade

  intent

*/

const DEFAULT_VALUES: Record<string, IForm_Color> = {
  primary: {
    color: '#2196f3',
    hue: 'blue',
    shade: 4,
  },
  secondary: {
    color: '#f50057',
    hue: 'pink',
    shade: 11,
  },
}

interface ColorToolProps {
  value: IForm_Color,
  intent: string,
  onUpdate: (value: IForm_Color) => void,
}

const ColorTool: FC<React.PropsWithChildren<ColorToolProps>> = ({
  value,
  intent = 'primary',
  onUpdate,
}) => {

  const classes = useStyles()
  const theme = useTheme()

  const fixedColors = useMemo<Record<string, string>>(() => {
    return {
      white: '#ffffff',
      black: '#000000',
      primary: theme.palette.primary.main,
      secondary: theme.palette.secondary.main,
    }
  }, [
    theme,
  ])

  const values = useMemo<Required<IForm_Color>>(
    () => {
      const givenValues = value || {}
      const defaultValues = DEFAULT_VALUES[intent]
      const ret = Object.assign({}, defaultValues, givenValues)
      if(!ret.inputColor) ret.inputColor = ret.color
      if(!ret.color) ret.color = defaultValues.color
      return ret as Required<IForm_Color>
    },
    [
      value,
      intent,
    ]
  )

  const {
    color,
    inputColor,
    shade,
  } = values

  const changeValues = useCallback(
    (updates: any) => onUpdate(Object.assign({}, value, updates)),
    [onUpdate,value]
  )

  const handleChangeColor = useCallback(
    (event: any) => {
      const {
        target: { value: color },
      } = event
      const newValues: IForm_Color = {
        inputColor: color,
      }
      if(isRgb(color)) {
        newValues.color = color
      }
      changeValues(newValues)
    },
    []
  )

  const handleChangeHue = useCallback(
    (event: any) => {
      const {
        target: { value: hue },
      } = event

      if(fixedColors[hue]) {
        changeValues({
          hue,
          color: fixedColors[hue],
          inputColor: fixedColors[hue],
        })
        return
      }
      if(!values.shade) return

      const shade = shades[values.shade]
      
      const color = colorMap[hue][shade]
      changeValues({
        hue,
        color,
        inputColor: color,
      })
    },
    [
      shades,
      values,
      fixedColors,
    ]
  )

  const handleChangeShade = useCallback(
    (event: any, shade: any) => {
      if(!values.hue) return
      if(fixedColors[values.hue]) return
      const shadeValue = shades[shade]
      const color = colorMap[values.hue][shadeValue]
      changeValues({
        shade,
        color,
        inputColor: color,
      })
    },
    [
      colors,
      values,
      shades,
    ]
  )

  const colorBar = useMemo(
    () => {
      const background = theme.palette.augmentColor({ mainShade: color, color: {main: color} })
      return (
        <Grid container className={ classes.colorBar }>
          {['dark', 'main', 'light'].map(key => {

            let colorString = ''

            if(key == 'dark') colorString = background.dark
            else if(key == 'main') colorString = background.main
            else if(key == 'light') colorString = background.light

            return (
              <div
                className={ classes.colorSquare }
                style={{ backgroundColor: colorString }}
                key={ key }
                onClick={ () => {
                  handleChangeColor({
                    target: {
                      value: rgbToHex(colorString)
                    },
                  })
                }}
              >
                <Typography
                  variant="caption"
                  style={{ color: theme.palette.getContrastText(colorString) }}
                >
                  { rgbToHex(colorString) }
                </Typography>
              </div>
            )  
          })}
        </Grid>
      )
    },
    [
      color,
    ]
  )

  const getColorSwatch = useCallback((backgroundColor: string, title: string) => {
    const contrastFromBlack = getContrastRatio('#000000', backgroundColor)
    return (
      <Tooltip placement="right" title={ title } key={ backgroundColor }>
        <Radio
          className={ classes.radio }
          color="default"
          checked={ color === backgroundColor }
          onChange={ handleChangeHue }
          value={ title }
          name={ intent }
          icon={<div className={ classes.radioIcon } style={{ backgroundColor }} />}
          checkedIcon={
            <div className={ classes.radioIconSelected } style={{ backgroundColor }}>
              <CheckIcon style={{ fontSize: 30, color: contrastFromBlack > 10 ? '#000' : '#fff' }} />
            </div>
          }
        />
      </Tooltip>
    )
  }, [
    color,
    classes,
  ])

  const colorSwatch = useMemo(
    () => {
      return (
        <div className={ classes.swatch }>
          {
            getColorSwatch('#ffffff', 'white')
          }
          {
            getColorSwatch('#000000', 'black')
          }
          {
            getColorSwatch(theme.palette.primary.main, 'primary')
          }
          {
            getColorSwatch(theme.palette.secondary.main, 'secondary')
          }
          {
            hues.map(hue => {
              const colors = colorMap[hue] || {} 
              const color = colors[shades[shade]] || '#000000'
              return getColorSwatch(color, hue)
            })
          }
        </div>
      )
    },
    [
      getColorSwatch,
      theme,
      color,
    ]
  )

  return (
    <div>
      
      { colorSwatch }

      <div className={ classes.sliderContainer }>
        <Typography id="ShadeSliderLabel">Shade:</Typography>
        <Slider
          className={ classes.slider }
          value={ shade }
          min={ 0 }
          max={ 13 }
          step={ 1 }
          onChange={ handleChangeShade }
          aria-labelledby="ShadeSliderLabel"
        />
        <Typography>{ shades[shade] }</Typography>
      </div>

      <Input
        id="color-value"
        value={ inputColor }
        onChange={ handleChangeColor }
        inputProps={{
          'aria-label': `Color`,
        }}
        fullWidth
      />
        
      { colorBar }
    </div>
   
  )
}

export default ColorTool